import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Ear, ArrowRight, Loader2 } from "lucide-react";
import api from "../lib/api";

/**
 * WeeklyFocus — a soft, encouraging Dashboard card that surfaces the top
 * grapheme misses so learners see the adaptive engine working for them.
 *
 * We show only when there's at least one recorded miss — a brand-new
 * learner isn't going to see "you're struggling with X." Every miss is
 * framed as a next step, never as a scoreboard.
 */
export default function WeeklyFocus() {
    const [state, setState] = useState("loading");
    const [focus, setFocus] = useState([]);
    const [playing, setPlaying] = useState(null);

    useEffect(() => {
        api.get("/stats/focus-graphemes")
            .then((r) => {
                setFocus(r.data.focus || []);
                setState((r.data.focus || []).length > 0 ? "ready" : "empty");
            })
            .catch(() => setState("empty"));
    }, []);

    if (state !== "ready") return null;

    const play = async (g) => {
        if (playing) return;
        setPlaying(g);
        try {
            const { data } = await api.post("/tts", { text: g, mode: "phoneme" });
            await new Audio(`data:${data.mime};base64,${data.audio_base64}`).play().catch(() => {});
        } catch {
            /* noop */
        } finally {
            setPlaying(null);
        }
    };

    const phonemeList = focus.map((f) => f.phoneme).join(" and ");

    return (
        <section
            className="card-rw p-6 md:p-7 mb-10"
            style={{ borderLeft: "6px solid #D47A55" }}
            data-testid="weekly-focus"
        >
            <div className="flex flex-wrap items-start gap-5">
                <div className="w-11 h-11 rounded-lg bg-[#FDE9DD] flex items-center justify-center flex-shrink-0">
                    <Ear className="w-5 h-5 text-[#D47A55]" strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-[240px]">
                    <p className="label-eyebrow mb-1" style={{ color: "#D47A55" }}>
                        This week&apos;s focus
                    </p>
                    <h3
                        className="text-2xl font-medium text-[#1C2321] mb-2"
                        style={{ fontFamily: "Outfit" }}
                        data-testid="weekly-focus-headline"
                    >
                        Your ears tripped on {phonemeList}.
                    </h3>
                    <p className="text-[#515B57] leading-relaxed mb-4">
                        We&apos;re quietly weaving these into your next games so you keep bumping
                        into them until they click. No extra work — just keep playing.
                    </p>

                    <div className="flex flex-wrap gap-2 mb-2" data-testid="weekly-focus-tiles">
                        {focus.map((f) => (
                            <button
                                key={f.grapheme}
                                onClick={() => play(f.grapheme)}
                                disabled={!!playing}
                                data-testid={`weekly-focus-tile-${f.grapheme}`}
                                aria-label={`Play the sound of ${f.display}`}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] font-medium hover:bg-[#F5F1E4] transition-colors disabled:opacity-60"
                                style={{ fontFamily: "Outfit" }}
                            >
                                {playing === f.grapheme ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[#D47A55]" strokeWidth={1.8} />
                                ) : (
                                    <span className="text-[#D47A55]" aria-hidden>◍</span>
                                )}
                                <span>{f.phoneme}</span>
                                {f.hint && (
                                    <span className="text-xs text-[#819888] font-normal">{f.hint}</span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
                <Link
                    to="/app/games/blending"
                    data-testid="weekly-focus-cta"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-sm font-medium hover:bg-[#1F4237] self-start"
                >
                    Practice these
                    <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                </Link>
            </div>
        </section>
    );
}
